import { registerCapabilities } from "@loumy/capabilities";
import type { BuildPipelineContext, PriceBook } from "@loumy/contracts";
import {
  ConfigurableImageEditProvider,
  ConfigurableImageProvider,
  FalBen2Provider,
  FalBriaProvider,
  FalEsrganProvider,
  FalFilmProvider,
  FalRifeProvider,
  FalSeedVrProvider,
  FalVaceProvider,
  FalQwenImageEditProvider,
  FalQwenImageProvider,
  FalVideoProvider,
  FalVisionProvider,
  GeminiVisionProvider,
  InMemoryProviderRegistry,
  PixverseVideoProvider,
} from "@loumy/providers";
import { InMemoryCapabilityRegistry } from "@loumy/registry";

// §4 „Credits-Vorschau: pricingEngine.price(plan) aus buildPipeline(...,ctx)". Die UI
// baut den Plan mit DENSELBEN reinen Engines wie der Server (SSOT bis ins Frontend) —
// Provider hier KEYLESS (nur Limits/`resolve`, kein Secret im Browser; `generate`
// bliebe ohne Key absichtlich verschlossen). Nur für die Vorschau; der echte Hold/
// Estimate entsteht serverseitig im Submit (D34).

/** Provider-Registry mit den Default-Adaptern (Limits + deterministisches resolve). */
function makePreviewProviders(): InMemoryProviderRegistry {
  const providers = new InMemoryProviderRegistry();
  // Default-Reihenfolge MUSS dem Server (apps/shared/registries.ts) entsprechen, sonst
  // driften Vorschau-Estimate und serverseitiger Hold (D34): fal.ai = Default (D57), dann
  // Pixverse-Direkt als Swap. v6-Limits beider sind identisch — Preis/Bounds bleiben gleich.
  providers.register("video", new FalVideoProvider());
  providers.register("video", new PixverseVideoProvider());
  // image-Default = Qwen Image 2.0 über fal.ai (D63/D76), dann ConfigurableImageProvider als
  // Swap — IDENTISCH zum Server (registries.ts). Qwen nimmt jetzt 1–3 Referenzbilder (Edit-
  // Endpoint, D76 maxRefs:3) ⇒ die Browser-Vorschau und der Server akzeptieren dieselben Refs.
  providers.register("image", new FalQwenImageProvider());
  providers.register("image", new ConfigurableImageProvider());
  // vision-Default = Google Gemini DIREKT (D60), dann fal-Aggregator als Swap — muss dem Server
  // (registries.ts) entsprechen, sonst driftet die Credits-Vorschau (D34). judge() wird in der
  // Vorschau nie gerufen (nur Plan-Build/Pricing); der `llm`-Slot (prompt-enhance) wird im Build
  // nicht aufgelöst (run-only, grazile Degradierung) → hier bewusst nicht registriert.
  providers.register("vision", new GeminiVisionProvider());
  providers.register("vision", new FalVisionProvider());
  // image-edit-Default = Qwen-Edit (D76), Swap = Configurable — wie der Server (registries.ts);
  // sonst pinnt der Build eine andere image-edit-id als zur Laufzeit (resolvePinned-Drift, D34).
  providers.register("image-edit", new FalQwenImageEditProvider());
  providers.register("image-edit", new ConfigurableImageEditProvider());
  // frame-interpolation (D94 — D122: der Default-Loop-Build pinnt den Slot WIEDER — Drop-last+
  // 1-Frame-RIFE-Naht; die Browser-Vorschau baut/preist denselben Pin und braucht die
  // Registrierung daher AKTIV). Default = RIFE (D122-Korrektur), FILM = Swap für Alt-Specs —
  // Reihenfolge identisch zum Server (registries.ts, D34).
  providers.register("frame-interpolation", new FalRifeProvider());
  providers.register("frame-interpolation", new FalFilmProvider());
  // video-inpaint (D131): der loop=on-Build pinnt den Slot (VACE-Naht im Trim-Modus) — die
  // Browser-Vorschau baut/preist denselben Pin und braucht die Registrierung daher AKTIV.
  // Reihenfolge identisch zum Server (registries.ts, D34).
  providers.register("video-inpaint", new FalVaceProvider());
  // D146: bg-remove-Slots (BEN2/Bria) — der Build pinnt sie bei method/bgRemove='ai' bzw. fürs
  // bg-remove-Tool; die Browser-Vorschau baut/preist denselben Pin und braucht die Registrierung
  // daher AKTIV (sonst wirft resolve → AI-Pfade brechen). Identisch zum Server (registries.ts, D34).
  providers.register("video-bg-remove", new FalBen2Provider());
  providers.register("image-bg-remove", new FalBriaProvider());
  // D163: image-upscale-Slot (Real-ESRGAN) — der Build pinnt ihn fürs image-upscale-Tool; die
  // Browser-Vorschau baut/preist denselben Pin und braucht die Registrierung daher AKTIV.
  // Identisch zum Server (registries.ts, D34).
  providers.register("image-upscale", new FalEsrganProvider());
  // D168: video-enhance-Slot (SeedVR2) — der Build pinnt ihn bei enhanceQuality=true; die
  // Browser-Vorschau baut/preist denselben Pin und braucht die Registrierung daher AKTIV
  // (Codex-D168-P1: ohne sie warf resolve → estimate null → Generate-Button gesperrt).
  // Identisch zum Server (registries.ts, D34).
  providers.register("video-enhance", new FalSeedVrProvider());
  return providers;
}

function makePreviewCapabilities(): InMemoryCapabilityRegistry {
  const capabilities = new InMemoryCapabilityRegistry();
  registerCapabilities(capabilities);
  return capabilities;
}

export function makePreviewContext(now: string): BuildPipelineContext {
  return {
    providers: makePreviewProviders(),
    capabilities: makePreviewCapabilities(),
    priceBookVersion: PREVIEW_PRICE_BOOK.version,
    now,
  };
}

// Spiegelt das aktuell AKTIVE Price-Book **v12** (= Seed v1 + `loop`, D94/Migr. 0022, +
// `video-gen#fal-kling`, D119/Migr. 0025, + `loop#fal-vace`, D131/Migr. 0030, +
// `video-encode`, D132/Migr. 0032, + `timeline-cut`/`frames-select-segments`, D139/Migr. 0035,
// + D146/Migr. 0037, + D150/Migr. 0039, + D151/Migr. 0041 (v10: `frame-smooth`), + D163/Migr. 0044
// (v11: `image-upscale`), + D168/Migr. 0045 (v12: `video-enhance` flat 6 + `frames-resize` 0);
// identisch zu price-book.fixture). D150: KOSTENDECKENDES Modell —
// Aktionspreis = reale COGS (1 cr = $0,01 COGS), Marge wandert in den Credit-KAUF.
// Reine, NICHT-autoritative Vorschau; der verbindliche Estimate/Hold entsteht serverseitig beim
// Submit aus dem aktiven Book (§9.7/D34 — `version` hier MUSS dem aktiven Buch folgen, sonst
// Preview/Server-Drift, Codex-P2). Fail-closed-Pricing (D44) braucht JEDE Capability bepreist.
export const PREVIEW_PRICE_BOOK: PriceBook = {
  version: 12,
  capabilityCost: {
    "input-validate": 1,
    "input-normalize": 2,
    "prompt-enhance": 0,
    // D150: auflösungsabhängig — Basis 5/s (360p) + additive resolution-Aufschläge ⇒
    // 540p 7 · 720p 9 · 1080p 18 cr/s (reale Pixverse-V6-on-demand-COGS, D55).
    "video-gen": {
      sum: [
        { perUnit: 5, unit: "second" },
        { perUnit: 2, unit: "second", when: { param: "resolution", eq: "540p" } },
        { perUnit: 4, unit: "second", when: { param: "resolution", eq: "720p" } },
        { perUnit: 13, unit: "second", when: { param: "resolution", eq: "1080p" } },
        { perUnit: 2, unit: "second", when: { param: "audio", eq: true } },
      ],
    },
    // D119→D125: ALTLAST — Loumy/Kling entfernt; Key bleibt (aktives Book v4 immutabel,
    // Spiegel-Treue — loop-fix-Muster).
    "video-gen#fal-kling": {
      sum: [
        { perUnit: 12, unit: "second" },
        { perUnit: 6, unit: "second", when: { param: "audio", eq: true } },
      ],
    },
    "image-gen": { sum: [{ perUnit: 4, unit: "megapixel" }] },
    // D146: chroma-key ist jetzt die GRATIS-Option (war flat 2; Migr. 0037, v8).
    "chroma-key": { sum: [{ perUnit: 0, unit: "flat" }] },
    "loop-fix": { sum: [{ perUnit: 3, unit: "flat" }] }, // DEPRECATED (D94/D32) — Eintrag bleibt für alte Specs
    // D94: Loop-Cut (GPU) + FILM-Naht (fal.ai, 1–2 Frames) — PLACEHOLDER wie alle Zahlen hier.
    loop: { sum: [{ perUnit: 4, unit: "flat" }] },
    // D131: Trim-Modus mit VACE-Naht-Inpainting (Wan 2.2 VACE Fun A14B — D134-Amendment, 1.3B
    // von fal zurückgezogen) — provider-bewusster Key (D101-Kette schlägt 'loop'). Flat 6 war
    // auf das billige 1.3B kalkuliert; A14B-COGS ≈ $0.25/Naht ⇒ Rekalibrierung = §13-Pricing.
    "loop#fal-vace": { sum: [{ perUnit: 25, unit: "flat" }] }, // D150: 6→25 (reale A14B-COGS ~$0,25/Naht)
    "transparency-crop": { sum: [{ perUnit: 1, unit: "flat" }] },
    // D139: Studio-Timeline-Schnitt — flat 1, EDIT-Preis-Term (steps) UND Job-Capability
    // (studio-export schneidet server-seitig identisch). Schnitt-only-Sheet = timeline-cut(1) +
    // spritesheet-pack(1) = 2; Einzelframe-PNG = timeline-cut(1) = 1; Key+Schnitt-Sheet = 5.
    "timeline-cut": { sum: [{ perUnit: 1, unit: "flat" }] },
    "frames-select-segments": { sum: [{ perUnit: 1, unit: "flat" }] },
    "frame-interpolate": { sum: [{ perUnit: 1, unit: "frame" }] },
    // D151 (Migr. 0041, v10): „Flüssige 24 FPS" (frame-smooth) — Dedup naher Duplikat-Frames +
    // RIFE-Reinterpolation (fal-ai/rife, paarweise). GENERISCHER Key (provider-agnostisch): der
    // Flat ist für jede RIFE-Klasse-Interpolation gleich, daher KEIN `#provider`-Suffix — die
    // Fallback-Kette (price.ts) deckt jeden gepinnten Interpolations-Adapter ab. Flat 4 ≈ reale
    // fal-rife-COGS eines typischen Loops (~$0,04, kostendeckendes D150-Modell); §13-kalibrierbar
    // (echte fal-rife-COGS per Live-Smoke), wie loop 6→25. Bei nichts-zu-deduplizieren (3D-Look)
    // ist der Knoten ein No-op, der Flat fällt dennoch (per-Knoten-No-op-Refund = §13).
    "frame-smooth": { sum: [{ perUnit: 4, unit: "flat" }] },
    "spritesheet-pack": { sum: [{ perUnit: 1, unit: "flat" }] },
    // D146: video-encode ebenfalls GRATIS (Teil des Chroma-Video-Pfads; war flat 2; Migr. 0037).
    "video-encode": { sum: [{ perUnit: 0, unit: "flat" }] },
    // D146/D150: AI-Matting (BEN2/Bria via fal) — KOSTENDECKEND statt Premium-Aktionspreis
    // (BEN2 ~$0,02, Bria ~$0,018). „Teurer als Chroma" bleibt (2 > 0); Premium wandert in den Kaufpreis.
    "video-bg-remove": { sum: [{ perUnit: 2, unit: "flat" }] },
    "image-bg-remove": { sum: [{ perUnit: 2, unit: "flat" }] },
    // D163 (Migr. 0044, v11): Super-Resolution via Real-ESRGAN (fal). GENERISCHER Key (provider-
    // agnostisch — der Flat gilt für jede Super-Resolution-Klasse; die price()-Fallback-Kette deckt
    // den gepinnten 'fal-esrgan'-Adapter ab). Flat 2 ≈ reale ESRGAN-COGS (~$0,01–0,02/Bild,
    // kostendeckendes D150-Modell), wie image-bg-remove; §13-kalibrierbar per Live-Smoke.
    "image-upscale": { sum: [{ perUnit: 2, unit: "flat" }] },
    // D168 (Migr. 0045, v12): SeedVR2-Qualitäts-Pass — generischer Key, flat 6 ($0,001/MPx
    // Output ⇒ ~$0,10–0,25/Clip); frames-resize = Downscale-back, Teil des Passes (flat 0).
    "video-enhance": { sum: [{ perUnit: 6, unit: "flat" }] },
    "frames-resize": { sum: [{ perUnit: 0, unit: "flat" }] },
  },
  toolBaseFee: { spritesheet: 5, "image-generator": 2, "chroma-key-remover": 0, "bg-remove": 0, "image-upscale": 0 },
};
